import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { Crown, Check, AlertCircle, Clock, ArrowUp, ArrowDown, Users, Plus, Minus, TrendingUp, Mail, DollarSign } from 'lucide-react';
import { useFeatures } from './FeatureContext';
import { useTheme } from './ThemeContext';

export default function SubscriptionSettings({ companyId }) {
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [seatsToBuy, setSeatsToBuy] = useState(1);
  const [buyingSeats, setBuyingSeats] = useState(false);
  const [displayInterval, setDisplayInterval] = useState(null); // For viewing different pricing (doesn't change plan)
  const [actualTeamCount, setActualTeamCount] = useState(null); // Fresh count from DB
  const [referralCreditsCents, setReferralCreditsCents] = useState(0); // Referral credits in current period

  const { canPurchaseMoreSeats, getRemainingPurchasableSeats, getLimit, refresh: refreshFeatures, teamCount } = useFeatures();

  // Seat pricing (varies by billing interval)
  const SEAT_PRICING = {
    weekly: 4.00,
    monthly: 15.00,
    annual: 150.00
  };
  // Actual billing interval from company (for charges summary)
  const billingInterval = company?.billing_interval || 'monthly';
  // Display interval is what's shown in the UI (can be different from actual)
  const viewingInterval = displayInterval || billingInterval;
  const SEAT_PRICE = SEAT_PRICING[viewingInterval] || SEAT_PRICING.monthly;

  // Pricing tiers - same pricing for all businesses
  const tiers = {
    free: {
      name: 'Free',
      weeklyPrice: 0,
      monthlyPrice: 0,
      annualPrice: 0,
      calendars: 1,
      baseUsers: 1,
      maxExtraSeats: 0,
      users: '1 user',
      features: ['Accept customer payments', '1 calendar', '1 user', 'Up to 25 customers', 'Basic invoicing'],
      color: '#7f8c8d'
    },
    pro: {
      name: 'Pro',
      weeklyPrice: 12,
      monthlyPrice: 50,
      annualPrice: 500,
      calendars: 1,
      baseUsers: 1,
      maxExtraSeats: 2,
      users: '1 user (+2 available)',
      features: ['1 calendar', '1 user included', 'Up to 2 extra seats', 'Quote builder', 'Invoice management'],
      color: colors.accent
    },
    team: {
      name: 'Team',
      weeklyPrice: 25,
      monthlyPrice: 100,
      annualPrice: 1000,
      calendars: 5,
      baseUsers: 3,
      maxExtraSeats: 2,
      users: '3 users (+2 available)',
      features: ['5 calendars', '3 users included', 'Up to 2 extra seats', 'Team management', 'SMS texting', 'Priority support'],
      color: '#3498db'
    },
    growth: {
      name: 'Growth',
      weeklyPrice: 50,
      monthlyPrice: 200,
      annualPrice: 2000,
      calendars: -1,
      baseUsers: 5,
      maxExtraSeats: 5,
      users: '5 users (+5 available)',
      features: ['Unlimited calendars', '5 users included', 'Up to 5 extra seats', 'All features', 'API access', 'Premium support'],
      color: '#9b59b6'
    },
    business: {
      name: 'Business',
      weeklyPrice: 125,
      monthlyPrice: 500,
      annualPrice: 5000,
      calendars: -1,
      baseUsers: 10,
      maxExtraSeats: 10,
      flexDays: 30,
      users: '10 users (+10 available)',
      features: ['Unlimited calendars', '10 users included', 'Up to 10 extra seats', '30 flex-worker days/month', '1099 contractor support', 'Dedicated support'],
      color: '#e74c3c'
    },
    enterprise: {
      name: 'Enterprise',
      weeklyPrice: null,
      monthlyPrice: null,
      annualPrice: null,
      calendars: -1,
      baseUsers: -1,
      maxExtraSeats: -1,
      flexDays: -1,
      users: 'Custom',
      features: ['Everything in Business', 'Unlimited users', 'Unlimited flex days', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee'],
      color: '#2c3e50',
      contactUs: true
    }
  };

  // Helper to get price based on display interval (what user is viewing)
  const getTierPrice = (tier, interval = viewingInterval) => {
    if (!tier.monthlyPrice) return null;
    if (interval === 'weekly') return tier.weeklyPrice;
    if (interval === 'annual') return tier.annualPrice;
    return tier.monthlyPrice;
  };

  // Helper to get interval label
  const getIntervalLabel = (interval) => {
    if (interval === 'weekly') return '/wk';
    if (interval === 'annual') return '/yr';
    return '/mo';
  };

  useEffect(() => {
    loadData();
    refreshFeatures(); // Refresh cached team count in FeatureContext
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      if (!companyId) {
        setLoadError('No company ID available. Please try refreshing the page.');
        return;
      }

      // Calculate period start for referral credits query
      const now = new Date();
      const periodDays = 30; // approximate monthly billing cycle
      const periodStart = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000).toISOString();

      // Load company data, team count, and referral credits in parallel
      const [companyResult, countResult, creditsResult] = await Promise.all([
        supabaseData.from('companies').select('*').eq('id', companyId).single(),
        supabaseData.from('user_companies').select('*', { count: 'exact', head: true }).eq('company_id', companyId),
        supabaseData.from('referral_credits').select('credit_amount_cents').eq('referrer_company_id', companyId).gte('created_at', periodStart)
      ]);

      if (companyResult.error) throw companyResult.error;
      if (!companyResult.data) throw new Error('No company data returned');
      setCompany(companyResult.data);
      setActualTeamCount(countResult.count || 1);
      const totalCredits = (creditsResult.data || []).reduce((sum, c) => sum + c.credit_amount_cents, 0);
      setReferralCreditsCents(totalCredits);

    } catch (error) {
      setLoadError(error.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  // User count for messaging pricing - use fresh DB count, fall back to FeatureContext
  const messagingUserCount = Math.max(1, actualTeamCount ?? teamCount);

  const tierOrder = ['free', 'pro', 'team', 'growth', 'business', 'enterprise'];

  const handleTierChange = async (newTier) => {
    if (company?.tier === newTier && viewingInterval === billingInterval) return;

    const currentIndex = tierOrder.indexOf(company?.tier || 'pro');
    const newIndex = tierOrder.indexOf(newTier);
    const isUpgrade = newIndex > currentIndex || (newIndex === currentIndex && viewingInterval !== billingInterval);
    const newTierPrice = getTierPrice(tiers[newTier], viewingInterval);
    const priceLabel = getIntervalLabel(viewingInterval);
    const changingInterval = viewingInterval !== billingInterval;

    if (isUpgrade) {
      // UPGRADE: Takes effect immediately
      let message = `Upgrade to ${tiers[newTier].name} plan ($${newTierPrice}${priceLabel})?\n\n`;
      if (changingInterval) {
        message += `This also changes your billing to ${viewingInterval}.\n\n`;
      }
      message += `This change takes effect IMMEDIATELY and you'll be charged the prorated difference.`;

      if (!window.confirm(message)) return;

      setUpgrading(true);
      try {
        const updateData = {
          tier: newTier,
          max_calendars: tiers[newTier].calendars,
          pending_tier: null,
          tier_change_scheduled_at: null
        };
        if (changingInterval) {
          updateData.billing_interval = viewingInterval;
        }

        const { error } = await supabaseData
          .from('companies')
          .update(updateData)
          .eq('id', companyId);

        if (error) throw error;

        alert(`Successfully upgraded to ${tiers[newTier].name}! Your new features are available now.`);
        loadData();
        refreshFeatures();
      } catch (error) {
        alert('Error upgrading: ' + error.message);
      } finally {
        setUpgrading(false);
      }
    } else {
      // DOWNGRADE: Scheduled for next billing period
      const nextBillingDate = getNextBillingDate();

      let message = `Downgrade to ${tiers[newTier].name} plan ($${newTierPrice}${priceLabel})?\n\n`;
      if (changingInterval) {
        message += `This also changes your billing to ${viewingInterval}.\n\n`;
      }
      message += `You'll keep your current ${tiers[company?.tier].name} features until your next billing date (${nextBillingDate.toLocaleDateString()}).\n\n`;
      message += `The downgrade will take effect automatically on that date.`;

      if (!window.confirm(message)) return;

      setUpgrading(true);
      try {
        const updateData = {
          pending_tier: newTier,
          tier_change_scheduled_at: nextBillingDate.toISOString()
        };
        if (changingInterval) {
          updateData.billing_interval = viewingInterval;
        }

        const { error } = await supabaseData
          .from('companies')
          .update(updateData)
          .eq('id', companyId);

        if (error) throw error;

        alert(`Downgrade scheduled! You'll switch to ${tiers[newTier].name} on ${nextBillingDate.toLocaleDateString()}.`);
        loadData();
        refreshFeatures();
      } catch (error) {
        alert('Error scheduling downgrade: ' + error.message);
      } finally {
        setUpgrading(false);
      }
    }
  };

  const [canceling, setCanceling] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const handleOpenBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const resp = await fetch(`${window.location.origin}/api/billing-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          returnUrl: `${window.location.origin}/#settings`,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.url) {
        alert(data.error || 'Could not open billing portal — please try again or contact support.');
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      alert('Could not open billing portal: ' + err.message);
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    const confirmed = window.confirm(
      'Cancel your subscription?\n\n' +
      'You\'ll keep access until the end of your current billing period, ' +
      'then your plan ends and you won\'t be billed again. Your data stays ' +
      'intact — you can resubscribe anytime.'
    );
    if (!confirmed) return;

    setCanceling(true);
    try {
      const resp = await fetch(`${window.location.origin}/api/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        alert(data.error || 'Could not cancel your subscription. Please contact support.');
        return;
      }
      alert(data.message || 'Your subscription has been canceled.');
      loadData();
    } catch (err) {
      alert('Could not cancel your subscription: ' + err.message);
    } finally {
      setCanceling(false);
    }
  };

  const cancelPendingDowngrade = async () => {
    if (!window.confirm('Cancel your scheduled downgrade and keep your current plan?')) return;

    setUpgrading(true);
    try {
      const { error } = await supabaseData
        .from('companies')
        .update({
          pending_tier: null,
          tier_change_scheduled_at: null
        })
        .eq('id', companyId);

      if (error) throw error;

      alert('Downgrade cancelled! You\'ll stay on your current plan.');
      loadData();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setUpgrading(false);
    }
  };

  const getNextBillingDate = () => {
    // Calculate next billing date based on billing_interval
    const now = new Date();
    const interval = company?.billing_interval || 'monthly';

    if (interval === 'annual') {
      // Next year from subscription start
      const nextYear = new Date(now);
      nextYear.setFullYear(nextYear.getFullYear() + 1);
      return nextYear;
    } else if (interval === 'weekly') {
      // Next week
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);
      return nextWeek;
    } else {
      // Monthly - first of next month
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      return nextMonth;
    }
  };

  // Handle buying additional seats
  const handleBuySeats = async () => {
    if (seatsToBuy < 1) return;

    const seatCost = seatsToBuy * (SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly);
    const interval = getIntervalLabel(billingInterval);
    const confirmed = window.confirm(
      `Add ${seatsToBuy} team seat${seatsToBuy > 1 ? 's' : ''}?\n\nCost: $${seatCost.toFixed(2)}${interval}\n\nThis will be added to your next billing cycle.`
    );

    if (!confirmed) return;

    setBuyingSeats(true);
    try {
      const currentSeats = company?.purchased_seats || 0;
      const newTotal = currentSeats + seatsToBuy;

      const { error } = await supabaseData
        .from('companies')
        .update({ purchased_seats: newTotal })
        .eq('id', companyId);

      if (error) throw error;

      alert(`Successfully added ${seatsToBuy} seat${seatsToBuy > 1 ? 's' : ''}! You now have ${newTotal} extra seat${newTotal > 1 ? 's' : ''}.`);
      setSeatsToBuy(1);
      loadData();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setBuyingSeats(false);
    }
  };

  // Handle removing seats
  const handleRemoveSeats = async (seatsToRemove) => {
    const currentSeats = company?.purchased_seats || 0;
    if (currentSeats < seatsToRemove) return;

    const seatCost = SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly;
    const interval = getIntervalLabel(billingInterval);

    const confirmed = window.confirm(
      `Remove ${seatsToRemove} extra seat${seatsToRemove > 1 ? 's' : ''}?\n\nThis will reduce your cost by $${(seatsToRemove * seatCost).toFixed(2)}${interval}.`
    );

    if (!confirmed) return;

    try {
      const newTotal = Math.max(0, currentSeats - seatsToRemove);

      const { error } = await supabaseData
        .from('companies')
        .update({ purchased_seats: newTotal })
        .eq('id', companyId);

      if (error) throw error;

      alert(`Removed ${seatsToRemove} seat${seatsToRemove > 1 ? 's' : ''}. Takes effect next billing cycle.`);
      loadData();
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading subscription info...
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <AlertCircle size={32} style={{ color: '#e74c3c', marginBottom: '1rem' }} />
        <p style={{ color: colors.text, marginBottom: '0.5rem' }}>Could not load subscription info</p>
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '1rem' }}>{loadError}</p>
        <button
          onClick={loadData}
          style={{
            padding: '0.75rem 1.5rem',
            background: colors.accent,
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  const currentTier = tiers[company?.tier] || tiers.pro;
  const pendingTier = company?.pending_tier ? tiers[company.pending_tier] : null;
  const daysLeft = company?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(company.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Demo account: show instant tier switcher instead of checkout flow
  if (company?.is_demo) {
    const handleDemoTierChange = async (newTier) => {
      try {
        await supabaseData
          .from('companies')
          .update({ tier: newTier })
          .eq('id', companyId);
        loadData();
        refreshFeatures();
      } catch (err) {
        alert('Error: ' + err.message);
      }
    };

    return (
      <div>
        <div style={{
          background: colors.bg,
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '2px solid #F05A28'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <Crown size={24} style={{ color: colors.accent }} />
            <h2 style={{ margin: 0, color: colors.text, fontSize: '1.5rem' }}>
              {currentTier.name} Plan
            </h2>
            <span style={{
              background: colors.accent,
              color: '#fff',
              padding: '0.25rem 0.75rem',
              borderRadius: '999px',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}>
              DEMO ACCOUNT
            </span>
          </div>
          <p style={{ color: colors.textMuted, marginBottom: '1.5rem' }}>
            Demo accounts have all features unlocked. Switch tiers to preview different plan levels.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {Object.entries(tiers).filter(([k]) => !['free', 'business', 'enterprise'].includes(k)).map(([key, tier]) => (
              <button
                key={key}
                onClick={() => handleDemoTierChange(key)}
                style={{
                  flex: 1,
                  minWidth: '100px',
                  padding: '1rem',
                  background: company.tier === key ? tier.color + '30' : colors.bgCard,
                  border: company.tier === key ? `2px solid ${tier.color}` : `1px solid ${colors.border}`,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <div style={{ color: tier.color, fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {tier.name}
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  {tier.baseUsers} user{tier.baseUsers !== 1 ? 's' : ''}
                </div>
                {company.tier === key && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <Check size={16} style={{ color: tier.color }} />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // A period-end cancellation keeps subscription_status 'active' but sets
  // subscription_ends_at. Detect that so the UI can show it actually took.
  const hasValidCustomerId = company?.stripe_customer_id
    && !String(company.stripe_customer_id).startsWith('demo')
    && !String(company.stripe_customer_id).startsWith('test');
  const hasLiveSub = ['active', 'trialing', 'past_due', 'unpaid'].includes(company?.subscription_status);
  const pendingCancelAt = company?.subscription_ends_at && company?.subscription_status !== 'canceled'
    ? new Date(company.subscription_ends_at)
    : null;
  const isCancelPending = pendingCancelAt && pendingCancelAt.getTime() > Date.now();

  return (
    <div>
      {/* Manage billing — quick path to Stripe Customer Portal (payment method,
          invoices) plus in-app cancel. Shown whenever there is a customer to
          manage OR a live subscription to cancel. Cancellation works off the
          subscription id, so it must NOT depend on stripe_customer_id being
          linked (orphaned webhook records leave that null). */}
      {(hasValidCustomerId || hasLiveSub) && (
        <div style={{
          background: colors.bgInput,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}>
          <div style={{ minWidth: 200, flex: 1 }}>
            <div style={{ color: colors.text, fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.2rem' }}>
              Manage your billing
            </div>
            <div style={{ color: colors.textMuted, fontSize: '0.82rem', lineHeight: 1.45 }}>
              Update your payment method, download invoices, or cancel — all on Stripe's secure portal.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {hasValidCustomerId && (
              <button
                onClick={handleOpenBillingPortal}
                disabled={openingPortal}
                style={{
                  padding: '0.6rem 1.1rem',
                  background: colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: openingPortal ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                }}
              >
                {openingPortal ? 'Opening…' : 'Manage in Stripe'}
                {!openingPortal && <span style={{ fontSize: '0.9rem' }}>→</span>}
              </button>
            )}
            {isCancelPending ? (
              <span style={{ color: colors.textMuted, fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                ✓ Cancels on {pendingCancelAt.toLocaleDateString()}
              </span>
            ) : company?.subscription_status !== 'canceled' && (
              <button
                onClick={handleCancelSubscription}
                disabled={canceling}
                style={{
                  padding: '0.6rem 1.1rem',
                  background: 'transparent',
                  border: `1px solid ${colors.error || '#e74c3c'}`,
                  borderRadius: '8px',
                  color: colors.error || '#e74c3c',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: canceling ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {canceling ? 'Canceling…' : 'Cancel subscription'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Current Plan & Charges Summary */}
      <div style={{
        background: colors.bg,
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        border: `2px solid ${currentTier.color}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Crown size={24} style={{ color: currentTier.color }} />
              <h2 style={{ margin: 0, color: colors.text, fontSize: '1.5rem' }}>
                {currentTier.name} Plan
              </h2>
              {company?.subscription_status === 'trialing' && (
                <span style={{
                  background: '#f39c12',
                  color: '#000',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '999px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}>
                  TRIAL
                </span>
              )}
            </div>
            <p style={{ margin: 0, color: colors.textMuted }}>
              {currentTier.calendars === -1 ? 'Unlimited' : currentTier.calendars} calendar{currentTier.calendars !== 1 ? 's' : ''} • {currentTier.baseUsers} user{currentTier.baseUsers !== 1 ? 's' : ''} included
            </p>
            {daysLeft > 0 && company?.subscription_status === 'trialing' && (
              <p style={{ margin: '0.5rem 0 0 0', color: '#f39c12', fontSize: '0.9rem' }}>
                <AlertCircle size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                {daysLeft} days left in trial
              </p>
            )}
          </div>
          <div style={{ color: currentTier.color, fontWeight: 'bold', fontSize: '0.9rem', textTransform: 'capitalize' }}>
            Billed {billingInterval}
          </div>
        </div>

        {/* Charges Summary */}
        <div style={{
          background: colors.bgCard,
          borderRadius: '8px',
          padding: '1rem',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <DollarSign size={18} style={{ color: '#4ecca3' }} />
            <span style={{ color: colors.text, fontWeight: 'bold' }}>Your Current Charges</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.text }}>
              <span>{currentTier.name} Plan ({billingInterval})</span>
              <span>${getTierPrice(currentTier, billingInterval)}{getIntervalLabel(billingInterval)}</span>
            </div>
            {(company?.purchased_seats || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.text }}>
                <span>Extra Seats ({company.purchased_seats})</span>
                <span>${((company.purchased_seats || 0) * (SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly)).toFixed(2)}{getIntervalLabel(billingInterval)}</span>
              </div>
            )}
            {company?.messaging_enabled && (() => {
              const msgPlan = company.messaging_plan_type || 'monthly';
              const msgFirstPrice = { weekly: 4, monthly: 15 };
              const msgAdditionalPrice = { weekly: 3, monthly: 12 };
              const firstCost = msgFirstPrice[msgPlan] || msgFirstPrice.monthly;
              const additionalCount = Math.max(0, messagingUserCount - 1);
              const additionalCost = additionalCount * (msgAdditionalPrice[msgPlan] || msgAdditionalPrice.monthly);
              const msgTotal = firstCost + additionalCost;
              const msgInterval = msgPlan === 'weekly' ? '/wk' : '/mo';
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.text }}>
                  <span>Messaging (1st user{additionalCount > 0 ? ` + ${additionalCount} additional` : ''})</span>
                  <span>${msgTotal.toFixed(2)}{msgInterval}</span>
                </div>
              );
            })()}
            {referralCreditsCents > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#22c55e' }}>
                <span>Referral Credits</span>
                <span>-${(referralCreditsCents / 100).toFixed(2)}</span>
              </div>
            )}
            <div style={{ borderTop: `1px solid ${colors.border}`, marginTop: '0.5rem', paddingTop: '0.5rem', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
              <span style={{ color: colors.text }}>Total</span>
              {(() => {
                const planCost = getTierPrice(currentTier, billingInterval);
                const seatsCost = (company?.purchased_seats || 0) * (SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly);
                const msgPlan = company?.messaging_plan_type || 'monthly';
                const msgFirstPrice = { weekly: 4, monthly: 15 };
                const msgAdditionalPrice = { weekly: 3, monthly: 12 };
                const msgCost = company?.messaging_enabled
                  ? (msgFirstPrice[msgPlan] || msgFirstPrice.monthly) + Math.max(0, messagingUserCount - 1) * (msgAdditionalPrice[msgPlan] || msgAdditionalPrice.monthly)
                  : 0;
                const sameBillingInterval = !company?.messaging_enabled || msgPlan === billingInterval;

                if (sameBillingInterval) {
                  // Same interval - can add together
                  return (
                    <span style={{ color: '#4ecca3' }}>
                      ${(planCost + seatsCost + msgCost).toFixed(2)}{getIntervalLabel(billingInterval)}
                    </span>
                  );
                } else {
                  // Different intervals - show separately
                  return (
                    <span style={{ color: '#4ecca3', textAlign: 'right' }}>
                      <div>${(planCost + seatsCost).toFixed(2)}{getIntervalLabel(billingInterval)}</div>
                      <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>+ ${msgCost.toFixed(2)}{getIntervalLabel(msgPlan)} messaging</div>
                    </span>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* View Pricing Toggle - just changes what's displayed, doesn't change plan */}
      <div style={{
        background: colors.bg,
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '2rem',
        border: `1px solid ${colors.border}`
      }}>
        <h3 style={{ margin: '0 0 0.5rem 0', color: colors.text, fontSize: '1.1rem' }}>
          View Plans by Billing Cycle
        </h3>
        <p style={{ margin: '0 0 1rem 0', color: colors.textMuted, fontSize: '0.85rem' }}>
          Compare pricing at different billing intervals
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {['weekly', 'monthly', 'annual'].map(interval => (
            <button
              key={interval}
              onClick={() => setDisplayInterval(interval)}
              style={{
                flex: 1,
                minWidth: '100px',
                padding: '0.75rem',
                background: viewingInterval === interval ? '#1a2a1a' : colors.bgCard,
                border: viewingInterval === interval ? '2px solid #4ecca3' : `1px solid ${colors.border}`,
                borderRadius: '8px',
                color: viewingInterval === interval ? '#4ecca3' : colors.textMuted,
                cursor: 'pointer',
                textAlign: 'center',
                position: 'relative',
                textTransform: 'capitalize'
              }}
            >
              <div style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{interval}</div>
              {interval === billingInterval && (
                <div style={{ fontSize: '0.7rem', color: '#4ecca3', marginTop: '0.25rem' }}>
                  (Your plan)
                </div>
              )}
              {interval === 'annual' && (
                <div style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  background: '#4ecca3',
                  color: '#000',
                  padding: '0.15rem 0.4rem',
                  borderRadius: '4px',
                  fontSize: '0.65rem',
                  fontWeight: 'bold'
                }}>
                  Best Value
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Pending Downgrade Notice */}
      {pendingTier && company?.tier_change_scheduled_at && (
        <div style={{
          background: '#f39c1220',
          border: '2px solid #f39c12',
          borderRadius: '12px',
          padding: '1.25rem',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
            <Clock size={24} color="#f39c12" style={{ flexShrink: 0, marginTop: '2px' }} />
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#f39c12' }}>
                Downgrade Scheduled
              </h4>
              <p style={{ margin: 0, color: colors.text, fontSize: '0.9rem' }}>
                Your plan will change from <strong>{currentTier.name}</strong> to <strong>{pendingTier.name}</strong> on{' '}
                <strong>{new Date(company.tier_change_scheduled_at).toLocaleDateString()}</strong>.
              </p>
              <p style={{ margin: '0.5rem 0 0 0', color: colors.textMuted, fontSize: '0.85rem' }}>
                You'll continue to have access to all {currentTier.name} features until then.
              </p>
            </div>
            <button
              onClick={cancelPendingDowngrade}
              disabled={upgrading}
              style={{
                padding: '0.5rem 1rem',
                background: 'transparent',
                border: '1px solid #f39c12',
                borderRadius: '6px',
                color: '#f39c12',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                cursor: upgrading ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Cancel Downgrade
            </button>
          </div>
        </div>
      )}

      {/* Plan Options */}
      <h3 style={{ color: colors.text, marginBottom: '1rem' }}>
        Change Your Plan
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {Object.entries(tiers).map(([key, tier]) => {
          const isCurrent = company?.tier === key;
          const currentIndex = tierOrder.indexOf(company?.tier || 'pro');
          const keyIndex = tierOrder.indexOf(key);
          const isUpgrade = keyIndex > currentIndex;
          const isDowngrade = keyIndex < currentIndex;
          const isPendingDowngrade = company?.pending_tier === key;

          return (
            <div
              key={key}
              style={{
                background: isCurrent ? '#1a2a1a' : isPendingDowngrade ? '#f39c1210' : colors.bg,
                borderRadius: '12px',
                padding: '1.5rem',
                border: isCurrent ? `2px solid ${tier.color}` : isPendingDowngrade ? '2px solid #f39c12' : `1px solid ${colors.border}`
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <h4 style={{ margin: 0, color: tier.color, fontSize: '1.25rem' }}>{tier.name}</h4>
                {isCurrent && (
                  <Check size={18} style={{ color: '#4ecca3' }} />
                )}
                {isPendingDowngrade && (
                  <Clock size={16} style={{ color: '#f39c12' }} />
                )}
              </div>
              <p style={{ color: colors.text, fontSize: '1.5rem', fontWeight: 'bold', margin: '0.5rem 0' }}>
                {getTierPrice(tier) ? (
                  <>${getTierPrice(tier)}<span style={{ color: colors.textMuted, fontSize: '0.9rem', fontWeight: 'normal' }}>{getIntervalLabel(viewingInterval)}</span></>
                ) : (
                  <span style={{ fontSize: '1.1rem' }}>Custom Pricing</span>
                )}
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0', color: colors.textMuted }}>
                {tier.features.map((feature, i) => (
                  <li key={i} style={{ padding: '0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Check size={14} style={{ color: '#4ecca3' }} />
                    {feature}
                  </li>
                ))}
              </ul>
              {tier.contactUs ? (
                <button
                  onClick={() => window.location.href = 'mailto:sales@workvanapp.com?subject=Enterprise%20Plan%20Inquiry'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: tier.color,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Mail size={16} />
                  Contact Sales
                </button>
              ) : (
                <button
                  onClick={() => handleTierChange(key)}
                  disabled={isCurrent || isPendingDowngrade || upgrading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: isCurrent ? colors.bgInput : isPendingDowngrade ? colors.bgInput : isUpgrade ? tier.color : '#e74c3c',
                    color: isCurrent || isPendingDowngrade ? colors.textMuted : '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: isCurrent || isPendingDowngrade || upgrading ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {isCurrent ? (
                    'Current Plan'
                  ) : isPendingDowngrade ? (
                    'Scheduled'
                  ) : isUpgrade ? (
                    <>
                      <ArrowUp size={16} />
                      Upgrade Now
                    </>
                  ) : (
                    <>
                      <ArrowDown size={16} />
                      Downgrade
                    </>
                  )}
                </button>
              )}
              {isDowngrade && !isPendingDowngrade && !isCurrent && (
                <p style={{ margin: '0.5rem 0 0 0', color: colors.textMuted, fontSize: '0.75rem', textAlign: 'center' }}>
                  Takes effect next billing cycle
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Team Seats Add-on */}
      <h3 style={{ color: colors.text, marginBottom: '1rem', marginTop: '2rem' }}>
        <Users size={20} style={{ display: 'inline', marginRight: '0.5rem', color: '#3498db' }} />
        Team Seats
      </h3>
      <div style={{
        background: colors.bg,
        borderRadius: '12px',
        padding: '1.5rem',
        border: `1px solid ${colors.border}`
      }}>
        {/* Current Plan Seats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: `1px solid ${colors.borderLight}`,
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Plan Included</div>
            <div style={{ color: colors.text, fontSize: '1.25rem', fontWeight: 'bold' }}>
              {company?.max_users || 1} seat{(company?.max_users || 1) > 1 ? 's' : ''}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Extra Seats</div>
            <div style={{ color: '#4ecca3', fontSize: '1.25rem', fontWeight: 'bold' }}>
              +{company?.purchased_seats || 0} seat{(company?.purchased_seats || 0) !== 1 ? 's' : ''}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Total</div>
            <div style={{ color: colors.accent, fontSize: '1.25rem', fontWeight: 'bold' }}>
              {(company?.max_users || 1) + (company?.purchased_seats || 0)} seats
            </div>
          </div>
        </div>

        {/* Pro Plan Seat Limit Notice */}
        {company?.tier === 'pro' && (
          <div style={{
            background: getRemainingPurchasableSeats() === 0 ? '#4a2a1a' : '#1a2a3a',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            border: getRemainingPurchasableSeats() === 0 ? '1px solid #e74c3c' : '1px solid #3498db'
          }}>
            {getRemainingPurchasableSeats() === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <TrendingUp size={20} style={{ color: '#e74c3c' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: colors.text, fontWeight: 'bold' }}>
                    Pro Plan Seat Limit Reached
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                    Upgrade to Team or Unlimited for more seats
                  </div>
                </div>
                <button
                  onClick={() => handleTierChange('team')}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#3498db',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  Upgrade to Team
                </button>
              </div>
            ) : (
              <div style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                <span style={{ color: '#3498db', fontWeight: 'bold' }}>Pro Plan:</span> You can add up to {getRemainingPurchasableSeats()} more seat{getRemainingPurchasableSeats() !== 1 ? 's' : ''} at ${SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly}{getIntervalLabel(billingInterval)}.
                Need more than 5 total users? Upgrade to Team.
              </div>
            )}
          </div>
        )}

        {/* Extra Seats Cost */}
        {(company?.purchased_seats || 0) > 0 && (
          <div style={{
            background: '#1a2a1a',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <span style={{ color: colors.text }}>
              Extra seats: {company?.purchased_seats} × ${SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly}{getIntervalLabel(billingInterval)}
            </span>
            <span style={{ color: '#4ecca3', fontWeight: 'bold', fontSize: '1.1rem' }}>
              ${((company?.purchased_seats || 0) * (SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly)).toFixed(2)}{getIntervalLabel(billingInterval)}
            </span>
          </div>
        )}

        {/* Buy More Seats - Only show if can purchase more */}
        {canPurchaseMoreSeats() && (
          <div style={{
            background: colors.bgCard,
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.75rem' }}>
              Add Team Seats
              {company?.tier === 'pro' && (
                <span style={{ color: colors.textMuted, fontWeight: 'normal', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                  ({getRemainingPurchasableSeats()} available)
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  onClick={() => setSeatsToBuy(Math.max(1, seatsToBuy - 1))}
                  style={{
                    width: '44px',
                    height: '44px',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Minus size={16} />
                </button>
                <span style={{
                  width: '50px',
                  textAlign: 'center',
                  color: colors.text,
                  fontSize: '1.25rem',
                  fontWeight: 'bold'
                }}>
                  {seatsToBuy}
                </span>
                <button
                  onClick={() => {
                    const remaining = getRemainingPurchasableSeats();
                    setSeatsToBuy(Math.min(seatsToBuy + 1, remaining));
                  }}
                  disabled={seatsToBuy >= getRemainingPurchasableSeats()}
                  style={{
                    width: '44px',
                    height: '44px',
                    background: seatsToBuy >= getRemainingPurchasableSeats() ? colors.bgCard : colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: seatsToBuy >= getRemainingPurchasableSeats() ? colors.textMuted : colors.text,
                    cursor: seatsToBuy >= getRemainingPurchasableSeats() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Plus size={16} />
                </button>
              </div>

              <div style={{ color: colors.textMuted }}>
                × ${SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly}{getIntervalLabel(billingInterval)} =
              </div>

              <div style={{ color: colors.accent, fontWeight: 'bold', fontSize: '1.1rem' }}>
                ${(seatsToBuy * (SEAT_PRICING[billingInterval] || SEAT_PRICING.monthly)).toFixed(2)}{getIntervalLabel(billingInterval)}
              </div>

              <button
                onClick={handleBuySeats}
                disabled={buyingSeats || seatsToBuy > getRemainingPurchasableSeats()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#4ecca3',
                  color: '#000',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: buyingSeats ? 'not-allowed' : 'pointer',
                  opacity: buyingSeats ? 0.7 : 1,
                  marginLeft: 'auto'
                }}
              >
                {buyingSeats ? 'Adding...' : 'Add Seats'}
              </button>
            </div>
          </div>
        )}

        {/* Remove Seats (if any purchased) */}
        {(company?.purchased_seats || 0) > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '1rem',
            borderTop: `1px solid ${colors.borderLight}`
          }}>
            <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
              Need fewer seats?
            </span>
            <button
              onClick={() => handleRemoveSeats(1)}
              style={{
                padding: '0.5rem 1rem',
                background: 'transparent',
                color: '#e74c3c',
                border: '1px solid #e74c3c',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Remove 1 Seat
            </button>
          </div>
        )}
      </div>

      {/* Billing Info */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        background: colors.bg,
        borderRadius: '8px',
        border: `1px solid ${colors.border}`
      }}>
        <p style={{ color: colors.textMuted, fontSize: '0.85rem', margin: 0 }}>
          <strong style={{ color: colors.text }}>How billing works:</strong>
        </p>
        <ul style={{ color: colors.textMuted, fontSize: '0.85rem', margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
          <li><strong style={{ color: '#4ecca3' }}>Upgrades</strong> take effect immediately. You'll be charged the prorated difference.</li>
          <li><strong style={{ color: '#e74c3c' }}>Downgrades</strong> take effect at your next billing date. You keep current features until then.</li>
          <li>You can cancel a scheduled downgrade at any time before it takes effect.</li>
        </ul>
      </div>

      {/* Cancel subscription — graceful exit that stops billing without
          deleting the account. Hidden for already-canceled accounts. */}
      {company?.subscription_status !== 'canceled' && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: colors.bg,
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.75rem'
        }}>
          <div>
            <p style={{ color: colors.text, fontSize: '0.9rem', margin: 0, fontWeight: 600 }}>
              Cancel subscription
            </p>
            <p style={{ color: colors.textMuted, fontSize: '0.82rem', margin: '0.25rem 0 0 0' }}>
              Stops billing at the end of your current period. Your data stays — resubscribe anytime.
            </p>
          </div>
          {isCancelPending ? (
            <span style={{ color: colors.textMuted, fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
              ✓ Cancels on {pendingCancelAt.toLocaleDateString()}
            </span>
          ) : (
            <button
              onClick={handleCancelSubscription}
              disabled={canceling}
              style={{
                padding: '0.6rem 1.1rem',
                background: 'transparent',
                border: `1px solid ${colors.error || '#e74c3c'}`,
                borderRadius: '8px',
                color: colors.error || '#e74c3c',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: canceling ? 'wait' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {canceling ? 'Canceling…' : 'Cancel subscription'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
